import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { useEntity, GROUP_VALUE } from "@/lib/entityStore";
import { Card } from "@/components/ds/Card";
import { PageShell, PageHeader, Section, Loading } from "@/components/ds/PageShell";
import { Stat } from "@/components/ds/Stat";
import { Disclosure } from "@/components/ds/Disclosure";

export default function Reconciliation() {
    const { selectedEntityId } = useEntity();
    const [data, setData] = useState(null);

    useEffect(() => {
        const q = selectedEntityId === GROUP_VALUE ? "" : `?entity_id=${selectedEntityId}`;
        api.get(`/reconciliation${q}`).then((r) => setData(r.data));
    }, [selectedEntityId]);

    if (!data)
        return (
            <PageShell>
                <Loading label="Loading reconciliation…" testId="recon-loading" />
            </PageShell>
        );

    const isGroup = !!data.totals;
    const t = isGroup ? data.totals : data.summary;

    const matchRate = t.match_rate_pct;
    const headline =
        matchRate >= 80
            ? <>Reconciliation is healthy — <span className="text-emerald-700">{matchRate}%</span> match rate.</>
            : <>Match rate <span className="text-amber-700">{matchRate}%</span> — real cash truth not fully verified.</>;
    const detail =
        matchRate >= 80
            ? "Bank-to-MYOB matching is reliable. Spot-check exceptions below."
            : `${t.orphan_bank_txns} bank transactions have no matching MYOB payment, and ${t.orphan_payments} payments have no bank match. Address the orphans below.`;

    return (
        <PageShell width="wide" testId="reconciliation-page">
            <PageHeader
                eyebrow="Reconciliation engine"
                title="Reconciliation"
                subtitle="Deterministic matching across MYOB payments, bank-feed transactions and paid invoices. When the two sides don't agree, this is where you see exactly why."
            />

            <Card variant="default" padding="lg" className="mb-6" testId="recon-summary">
                <div className="label-soft mb-2 text-stone-500">Reconciliation pulse</div>
                <div className="text-display-md text-stone-900 leading-snug mb-2">{headline}</div>
                <p className="text-body text-stone-500 max-w-prose">{detail}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-5 mt-6">
                    <Stat label="Match rate" value={`${t.match_rate_pct}%`} money={false} size="sm" testId="kpi-match-rate" />
                    <Stat label="Recon score" value={`${t.reconciliation_score}/100`} money={false} size="sm" testId="kpi-recon-score" />
                    <Stat label="Matched" value={t.matched} money={false} size="sm" testId="kpi-matched" />
                    <Stat label="Partial" value={t.partial} money={false} size="sm" tone={t.partial > 5 ? "warning" : "default"} testId="kpi-partial" />
                    <Stat label="Timing off" value={t.timing_discrepancy} money={false} size="sm" tone={t.timing_discrepancy > 10 ? "warning" : "default"} testId="kpi-timing" />
                    <Stat label="Orphan payments" value={t.orphan_payments} money={false} size="sm" tone={t.orphan_payments > 0 ? "warning" : "default"} testId="kpi-orphan-pmt" />
                    <Stat label="Orphan bank" value={t.orphan_bank_txns} money={false} size="sm" tone={t.orphan_bank_txns > 10 ? "negative" : "default"} testId="kpi-orphan-bank" />
                    <Stat label="Duplicate suspects" value={t.duplicate_suspects} money={false} size="sm" tone={t.duplicate_suspects > 0 ? "negative" : "default"} testId="kpi-dupes" />
                </div>
            </Card>

            {!isGroup && (
                <Section
                    title="Exceptions"
                    subtitle="Open one to inspect the underlying rows."
                >
                    <Card variant="default" padding="md" className="space-y-1">
                        <Disclosure
                            title="Orphan bank transactions"
                            summary={`${data.orphan_bank_transactions?.length || 0} item${(data.orphan_bank_transactions?.length || 0) === 1 ? "" : "s"}`}
                            testId="disclosure-orphan-bank"
                        >
                            <TableShell
                                cols={["Date", "Amount", "Direction", "Merchant"]}
                                rows={(data.orphan_bank_transactions || []).slice(0, 100).map((r) => [
                                    fmtDate(r.date), fmtMoney(r.amount), r.direction, r.merchant,
                                ])}
                            />
                        </Disclosure>
                        <Disclosure
                            title="Orphan MYOB payments"
                            summary={`${data.orphan_payments?.length || 0} item${(data.orphan_payments?.length || 0) === 1 ? "" : "s"}`}
                            testId="disclosure-orphan-pmt"
                        >
                            <TableShell
                                cols={["Date", "Amount", "Direction", "Counterparty", "Reason"]}
                                rows={(data.orphan_payments || []).slice(0, 100).map((r) => [
                                    fmtDate(r.date), fmtMoney(r.amount), r.direction, r.counterparty, r.reason,
                                ])}
                            />
                        </Disclosure>
                        <Disclosure
                            title="Timing discrepancies"
                            summary={`${data.timing_discrepancies?.length || 0} item${(data.timing_discrepancies?.length || 0) === 1 ? "" : "s"}`}
                            testId="disclosure-timing"
                        >
                            <TableShell
                                cols={["Payment $", "Bank $", "Day delta", "Δ%", "Score"]}
                                rows={(data.timing_discrepancies || []).slice(0, 100).map((r) => [
                                    fmtMoney(r.amount), fmtMoney(r.bank_amount), `${r.day_delta} days`,
                                    `${r.amount_delta_pct}%`, r.score,
                                ])}
                            />
                        </Disclosure>
                        <Disclosure
                            title="Duplicate suspects"
                            summary={`${data.duplicate_suspects?.length || 0} item${(data.duplicate_suspects?.length || 0) === 1 ? "" : "s"}`}
                            testId="disclosure-dupe"
                        >
                            <TableShell
                                cols={["Date", "Amount", "Direction", "Count", "Merchant key"]}
                                rows={(data.duplicate_suspects || []).slice(0, 100).map((r) => [
                                    fmtDate(r.date), fmtMoney(r.amount), r.direction, r.count, r.merchant_key,
                                ])}
                            />
                        </Disclosure>
                    </Card>
                </Section>
            )}

            {isGroup && (
                <Section title="Per-entity reconciliation">
                    <Card variant="default" padding="none" className="overflow-hidden">
                        <table className="w-full text-[14px]">
                            <thead>
                                <tr className="text-left border-b border-stone-100 bg-stone-50/40">
                                    <th className="px-5 py-3 label-soft text-stone-500 font-medium">Entity</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Match rate</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Matched</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Orphan pmts</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Orphan bank</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Dupes</th>
                                    <th className="px-3 py-3 label-soft text-stone-500 font-medium text-right">Recon score</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.per_entity.map((p) => (
                                    <tr key={p.entity_id} className="border-b border-stone-100 last:border-0">
                                        <td className="px-5 py-3.5 text-stone-800">{p.display_name}</td>
                                        <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{p.summary.match_rate_pct}%</td>
                                        <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{p.summary.matched}</td>
                                        <td className={`px-3 py-3.5 font-mono-num text-right ${p.summary.orphan_payments > 0 ? "text-amber-700" : "text-stone-700"}`}>{p.summary.orphan_payments}</td>
                                        <td className={`px-3 py-3.5 font-mono-num text-right ${p.summary.orphan_bank_txns > 10 ? "text-red-700" : "text-stone-700"}`}>{p.summary.orphan_bank_txns}</td>
                                        <td className={`px-3 py-3.5 font-mono-num text-right ${p.summary.duplicate_suspects > 0 ? "text-red-700" : "text-stone-700"}`}>{p.summary.duplicate_suspects}</td>
                                        <td className="px-3 py-3.5 font-mono-num text-right text-stone-700">{p.summary.reconciliation_score}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Card>
                </Section>
            )}
        </PageShell>
    );
}

function TableShell({ cols, rows }) {
    if (!rows || rows.length === 0)
        return <div className="text-[13.5px] text-stone-500 py-2">No items.</div>;
    return (
        <div className="overflow-x-auto -mx-2">
            <table className="w-full text-[13px]">
                <thead>
                    <tr className="text-left border-b border-stone-100">
                        {cols.map((c) => (
                            <th key={c} className="px-2 py-2 label-soft text-stone-500 font-medium">{c}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} className="border-b border-stone-50 last:border-0">
                            {r.map((c, j) => (
                                <td key={j} className="px-2 py-2 font-mono-num text-stone-700">{c}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
